/**
 * useBuyingSession — load / create / save / end a single buying_sessions row.
 *
 * Day 54 architecture (see ADR Decisions 27/28/29):
 *
 * - param_snapshot: audit trail of params at session creation. Captured
 *   from useParamStore.allParams via buildParamSnapshot helper at
 *   createSession (Phase 5); re-captured at saveAsDraft to preserve audit
 *   trail across the live → draft transition per Sol adjudication
 *   2026-05-17 (draft IS the persisted entity in the lifecycle).
 *   Day 76 P5.1 closes Day 54 follow-up per BACKLOG topic-anchor
 *   "Day 54 follow-up: param_snapshot wiring" + OQ4 lock. The split
 *   between param_snapshot and ui_state is the two-JSONB model per ADR
 *   Decision 27 — different concerns, different update frequencies.
 * - ui_state: live editable session UI configuration. Patch-merged on every
 *   UI change.
 * - distributor_id: session-scoped (not derived from useApp().activeDistributor).
 *   Day 54 decouples this for BuyingSession only — every other surface keeps
 *   using activeDistributor from the global app context.
 *
 * Pre-Day-68 weight column mirror; columns dropped Day 77 P5.2. Engine
 * reads bswc.key directly via JOIN per ADR Decision 41/42 (Day 68 P3.1
 * refactor); session UI state lives in ui_state.weights JSONB per ADR
 * Decision 27 (Day 54). See ADR Decision 44 (Day 77 P5.2) for the
 * row-column drop attestation. `risk_inverse` was a row-only legacy
 * key, never wired into bswc.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { PostgrestError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import type { Json } from '../lib/database.types';
import { useApp } from './useApp';

// Day 80 P5.5 F46 — retry-once-with-150ms-delay around a Supabase write.
// Spec: "retry once before declaring divergence." Used by the three
// writebacks (updateUiState / updateSafetyCap / updateParamSnapshot) so
// transient network blips don't surface as user-visible errors. Accepts
// PromiseLike<T> because Supabase's PostgrestFilterBuilder is thenable
// but not a true Promise at the type level.
// Day 81.5 P2.F4 — `export` added for vitest compensating-control test
// (`src/test/day80HookSubstrate.test.ts`); same shape as Day 79 P5.4
// tierValidation.ts export pattern.
export async function withRetryOnce<T extends { error: PostgrestError | null }>(
  attempt: () => PromiseLike<T>
): Promise<T> {
  const first = await attempt();
  if (!first.error) return first;
  await new Promise((resolve) => setTimeout(resolve, 150));
  return await attempt();
}

// ── Types ──────────────────────────────────────────────────────────────────

/**
 * Day 58 — tier-mode session analysis. Persisted in ui_state JSONB via
 * existing patch-merge (no sidecar table, no schema migration). Single-channel
 * only (architectural Position 3 / Architecture Rule 16 grid budget); the
 * sidebar disables the Tier Mode toggle when the path tab is 'all'.
 */
export interface BuyingSessionTierMode {
  enabled: boolean;
  import_id: string | null;
  tiers: Array<{ qty_min: number; qty_max: number | null; unit_cost: number }>;
}

export interface BuyingSessionUiState {
  filters?: Record<string, string | undefined>;
  weights?: Record<string, { enabled: boolean; weight: string } | undefined>;
  constraints?: Record<string, string | undefined>;
  guardrails?: Record<string, string | undefined>;
  hidden_cols?: string[];
  column_presets?: Array<{ name: string; hidden: string[] }>;
  active_scenario_id?: string | null;
  /** Stored JSONB key — rides the legacy name until the marketplace enum dies
   *  (slice-B family). Page state reads/writes it as selectedPath. */
  marketplace?: string;
  filter_mode?: string;
  sort_key?: string | null;
  search_text?: string;
  tier_mode?: BuyingSessionTierMode;
  // Day 76 P5.1 B.2 — brand + manufacturer filter persistence.
  // Set<string> serializes as string[] via Array.from() on writeback,
  // hydrates via new Set() on session load (handled in BuyingSession.tsx).
  selected_brand?: string | null;
  only_branded?: boolean;
  matched_brands_only?: boolean;
  selected_mfr_ids?: string[];
  // Day 91b F62 — panel open state persistence (P7.5 part 2). Defaults
  // mirror BuyingSession.tsx React initial state: paramsPanelOpen=true,
  // detailPanelOpen=false, poTrayOpen=false. Missing key on hydration
  // short-circuits the setX call per R-Lock-13 typeof guard pattern.
  params_panel_open?: boolean;
  detail_panel_open?: boolean;
  po_tray_open?: boolean;
}

export interface BuyingSessionRecord {
  id: string;
  name: string | null;
  status: 'active' | 'draft' | 'completed' | 'abandoned';
  distributor_id: string | null;
  started_by: string | null;
  started_at: string;
  ended_at: string | null;
  param_snapshot: Record<string, unknown> | null;
  ui_state: BuyingSessionUiState | null;
  safety_cap_enabled: boolean;
}

export interface UseBuyingSessionResult {
  session: BuyingSessionRecord | null;
  loading: boolean;
  error: string | null;
  createSession: (args: {
    distributorId: string | null;
    paramSnapshot?: Record<string, unknown>;
  }) => Promise<BuyingSessionRecord>;
  updateUiState: (patch: Partial<BuyingSessionUiState>) => Promise<void>;
  // Day 76 P5.1 B.1 — safetyCap persistence to top-level column
  // (separate from ui_state JSONB per spec L496 lock).
  updateSafetyCap: (enabled: boolean) => Promise<void>;
  // Day 76 P5.1 — param_snapshot wiring closes Day 54 follow-up.
  // Consumed at createSession (Phase 5) + saveAsDraft re-capture.
  updateParamSnapshot: (snapshot: Record<string, unknown>) => Promise<void>;
  saveAsDraft: (name: string, paramSnapshot?: Record<string, unknown>) => Promise<void>;
  endSession: (status: 'completed' | 'abandoned') => Promise<void>;
  listMyDrafts: () => Promise<BuyingSessionRecord[]>;
  loadDraft: (sessionId: string) => Promise<BuyingSessionRecord>;
}

// ── Hook ───────────────────────────────────────────────────────────────────

/**
 * @param sessionId  When set, loads that session on mount. When null/undefined,
 *                   the hook starts in the "no session" state — caller can then
 *                   call createSession or loadDraft to populate.
 */
export function useBuyingSession(sessionId: string | null | undefined): UseBuyingSessionResult {
  const { user } = useApp();
  const [session, setSession] = useState<BuyingSessionRecord | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // T3.9 F-B fix — a ref mirroring `session` synchronously on every render. The
  // three fire-and-forget writebacks (updateUiState / updateSafetyCap /
  // updateParamSnapshot) read the current id/ui_state from HERE rather than
  // capturing them inside the setSession updater: under rapid successive updates
  // React does NOT invoke that updater synchronously (its eager-state computation
  // is skipped), so the prior capture left id/next null and the `if (!id) return`
  // SKIPPED the DB write — `buying_sessions.ui_state.filters` stayed `{}` under
  // the stage→clear-search→set-filter sequence. The ref read is always current.
  const sessionRef = useRef<BuyingSessionRecord | null>(session);
  sessionRef.current = session;

  // Auto-load on sessionId change.
  useEffect(() => {
    if (!sessionId) {
      setSession(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      const { data, error: e } = await supabase
        .from('buying_sessions')
        .select('*')
        .eq('id', sessionId)
        .maybeSingle();
      if (cancelled) return;
      if (e) {
        setError(e.message);
        setSession(null);
      } else if (!data) {
        setError('Session not found');
        setSession(null);
      } else {
        setSession(data as BuyingSessionRecord);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const createSession = useCallback(
    async (args: {
      distributorId: string | null;
      paramSnapshot?: Record<string, unknown>;
    }): Promise<BuyingSessionRecord> => {
      if (!user?.id) throw new Error('Cannot create session: no authenticated user');
      const initialUiState: BuyingSessionUiState = {
        filters: {},
        weights: {},
        constraints: {},
        guardrails: {},
        hidden_cols: [],
        column_presets: [],
        active_scenario_id: null,
        marketplace: 'all',
        filter_mode: 'all',
        sort_key: null,
        search_text: '',
        // Day 91b F62 — panel open defaults match BuyingSession.tsx
        // L607-609 React initial state (paramsPanelOpen=true; others=false).
        // Preserves "new sessions hydrate cleanly with all keys present"
        // semantic per R-Lock-12.
        params_panel_open: true,
        detail_panel_open: false,
        po_tray_open: false,
      };
      const { data, error: e } = await supabase
        .from('buying_sessions')
        .insert({
          status: 'active',
          started_by: user.id,
          distributor_id: args.distributorId,
          // ui_state is a freeform jsonb payload; the typed surface wants
          // Json so cast through unknown.
          ui_state: initialUiState as unknown as Json,
          // Day 76 P5.1 Phase 5 — paramSnapshot is optional; when provided
          // captures the full params hierarchy state at session-start
          // (per ADR Decision 27 audit-trail intent + Sol option (ii)
          // full-state lock 2026-05-17). Backward-compat: null when caller
          // doesn't pass a snapshot (matches pre-Day-76 behavior).
          param_snapshot: (args.paramSnapshot ?? null) as unknown as Json,
        })
        .select()
        .single();
      if (e || !data) throw new Error(e?.message || 'createSession failed');
      const row = data as BuyingSessionRecord;
      setSession(row);
      return row;
    },
    [user?.id]
  );

  const updateUiState = useCallback(async (patch: Partial<BuyingSessionUiState>): Promise<void> => {
    // Read the current session SYNCHRONOUSLY from the ref (the setSession updater
    // is NOT guaranteed to run synchronously — see sessionRef above). The
    // debounced caller's patch is a full ui_state snapshot, so the merge below
    // preserves every sibling key (filters + stage + search + the stored marketplace key + …).
    const prev = sessionRef.current;
    if (!prev) return;
    const id = prev.id;
    const next: BuyingSessionUiState = { ...(prev.ui_state || {}), ...patch };
    // Local-state-first: functional updater merges the patch into the LATEST
    // ui_state so concurrent writebacks don't clobber each other's siblings.
    setSession((s) => (s ? { ...s, ui_state: { ...(s.ui_state || {}), ...patch } } : s));
    const result = await withRetryOnce(() =>
      supabase
        .from('buying_sessions')
        .update({ ui_state: next as unknown as Json })
        .eq('id', id)
    );
    if (result.error) setError(result.error.message);
  }, []);

  // Day 76 P5.1 B.1 — safetyCap top-level column writeback.
  // Local-state-first mirrors updateUiState pattern; network write is
  // fire-and-forget from caller's perspective.
  const updateSafetyCap = useCallback(async (enabled: boolean): Promise<void> => {
    // T3.9 F-B fix — read id from the ref (same async-updater-capture bug class).
    const prev = sessionRef.current;
    if (!prev) return;
    const id = prev.id;
    setSession((s) => (s ? { ...s, safety_cap_enabled: enabled } : s));
    const result = await withRetryOnce(() =>
      supabase.from('buying_sessions').update({ safety_cap_enabled: enabled }).eq('id', id)
    );
    if (result.error) setError(result.error.message);
  }, []);

  // Day 76 P5.1 — param_snapshot writeback for re-capture at saveAsDraft
  // (Phase 5E lock: draft IS the persisted entity, params at draft-save
  // moment per Sol 2026-05-17). Cast through unknown for Json typing per
  // updateUiState precedent.
  const updateParamSnapshot = useCallback(
    async (snapshot: Record<string, unknown>): Promise<void> => {
      // T3.9 F-B fix — read id from the ref (same async-updater-capture bug class).
      const prev = sessionRef.current;
      if (!prev) return;
      const id = prev.id;
      setSession((s) => (s ? { ...s, param_snapshot: snapshot } : s));
      const result = await withRetryOnce(() =>
        supabase
          .from('buying_sessions')
          .update({ param_snapshot: snapshot as unknown as Json })
          .eq('id', id)
      );
      if (result.error) setError(result.error.message);
    },
    []
  );

  const saveAsDraft = useCallback(
    async (name: string, paramSnapshot?: Record<string, unknown>): Promise<void> => {
      if (!session?.id) throw new Error('Cannot saveAsDraft: no session');
      const trimmed = name.trim();
      // Day 76 P5.1 Phase 5 — paramSnapshot is optional re-capture (Sol
      // option (a) lock 2026-05-17: draft IS the persisted entity in the
      // lifecycle; capture-at-draft reflects buyer's commit-intent moment).
      // Conditional update preserves existing param_snapshot when caller
      // doesn't pass one (backward-compat for any consumer that calls
      // saveAsDraft without snapshot).
      const update: Record<string, unknown> = {
        status: 'draft',
        name: trimmed || null,
      };
      if (paramSnapshot) update.param_snapshot = paramSnapshot as unknown as Json;
      const { data, error: e } = await supabase
        .from('buying_sessions')
        .update(update)
        .eq('id', session.id)
        .select()
        .single();
      if (e) throw new Error(e.message);
      if (data) setSession(data as BuyingSessionRecord);
    },
    [session?.id]
  );

  const endSession = useCallback(
    async (status: 'completed' | 'abandoned'): Promise<void> => {
      if (!session?.id) return;
      const { data, error: e } = await supabase
        .from('buying_sessions')
        .update({ status, ended_at: new Date().toISOString() })
        .eq('id', session.id)
        .select()
        .single();
      if (e) throw new Error(e.message);
      if (data) setSession(data as BuyingSessionRecord);
    },
    [session?.id]
  );

  const listMyDrafts = useCallback(async (): Promise<BuyingSessionRecord[]> => {
    if (!user?.id) return [];
    const { data, error: e } = await supabase
      .from('buying_sessions')
      .select('*')
      .eq('started_by', user.id)
      .eq('status', 'draft')
      .order('updated_at', { ascending: false });
    if (e) throw new Error(e.message);
    return (data as BuyingSessionRecord[]) || [];
  }, [user?.id]);

  const loadDraft = useCallback(async (id: string): Promise<BuyingSessionRecord> => {
    setLoading(true);
    setError(null);
    const { data, error: e } = await supabase
      .from('buying_sessions')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    setLoading(false);
    if (e) {
      setError(e.message);
      throw new Error(e.message);
    }
    if (!data) {
      setError('Session not found');
      throw new Error('Session not found');
    }
    const row = data as BuyingSessionRecord;
    setSession(row);
    return row;
  }, []);

  return {
    session,
    loading,
    error,
    createSession,
    updateUiState,
    updateSafetyCap,
    updateParamSnapshot,
    saveAsDraft,
    endSession,
    listMyDrafts,
    loadDraft,
  };
}
